import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDimensionById } from '../../hooks/useDimension';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { usePipelineNode } from '../../hooks/usePipelineNode';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { dimensionService } from '../../services/dimension.service';
import type { Dimension } from '../../services/dimension.service';
import { useQueryClient } from '@tanstack/react-query';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/combobox';
import { Switch } from '../../components/ui/switch';
import { Loader2, Save, Ruler, ArrowLeft, Trash2, Info, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useIsReadOnly } from '../../contexts/ReadOnlyContext';
import { ReadOnlyBanner } from '../../components/ReadOnlyBanner';
import { useTemplateName } from '../../hooks/useTemplates';

export default function DimensionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: dimension, isLoading, error } = useDimensionById(id || '', { enabled: !!id });

  const isTemplateLocked = !!dimension?.managed_by_template_id;
  const templateName = useTemplateName(dimension?.managed_by_template_id || '');

  // Fetch all DIMENSION nodes (entities) for the entity selector
  const { data: nodesData, isLoading: nodesLoading } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
    filters: { node_type: 'DIMENSION' },
  });

  const isInDraftMode = useIsInDraftMode();
  const readOnly = useIsReadOnly();

  const [formData, setFormData] = useState<Partial<Dimension>>({
    name: '',
    column_name: '',
    description: '',
    pipeline_node_id: '',
    field_id: '',
    include_in_supersets: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch the selected node to get its fields
  const { data: selectedNode, isLoading: selectedNodeLoading } = usePipelineNode(
    formData.pipeline_node_id || null
  );

  const isDateDimension = selectedNode?.node_type === 'DATE_DIMENSION';

  // Build entity options for the combobox
  const entityOptions = useMemo(() => {
    if (!nodesData?.records) return [];
    return nodesData.records.map(node => ({
      value: node.id,
      label: node.label || node.name || node.id,
      description: node.description,
    }));
  }, [nodesData]);

  // Build column options from the selected node's fields (excluding FOREIGN_KEY types)
  const columnOptions = useMemo(() => {
    if (!selectedNode?.fields) return [];
    return selectedNode.fields
      .filter(field => field.type !== 'FOREIGN_KEY')
      .map(field => ({
        value: field.id || field.name || '',
        label: field.label || field.name || field.id || '',
        description: field.type,
      }));
  }, [selectedNode]);

  // Initialize form data when dimension loads
  useEffect(() => {
    if (dimension) {
      setFormData({
        name: dimension.name || '',
        column_name: dimension.column_name || '',
        description: dimension.description || '',
        pipeline_node_id: dimension.pipeline_node_id || '',
        field_id: dimension.field_id || '',
        include_in_supersets: dimension.include_in_supersets ?? false,
      });
      setHasChanges(false);
    }
  }, [dimension]);

  const handleInputChange = (field: keyof Dimension, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleEntityChange = (nodeId: string) => {
    setFormData(prev => ({
      ...prev,
      pipeline_node_id: nodeId,
      field_id: '', // Reset field when entity changes
    }));
    setHasChanges(true);
  };

  const handleColumnChange = (fieldId: string) => {
    setFormData(prev => ({
      ...prev,
      field_id: fieldId,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!id) return;

    setIsSaving(true);
    try {
      await dimensionService.save({
        id,
        ...formData,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['dimension', id] });
      queryClient.invalidateQueries({ queryKey: ['dimensions-list'] });
      queryClient.invalidateQueries({ queryKey: ['dimensions'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-node'] });

      setHasChanges(false);
      toast.success('Dimension saved successfully');
    } catch (err) {
      console.error('Failed to save dimension:', err);
      toast.error('Failed to save dimension');
    } finally {
      setIsSaving(false);
    }
  };

  useUnsavedChangesGuard(hasChanges, handleSave);

  const handleDelete = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      await dimensionService.delete(id);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['dimensions-list'] });
      queryClient.invalidateQueries({ queryKey: ['dimensions'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-node'] });

      toast.success('Dimension deleted successfully');
      navigate('/develop/semantic-layer/dimensions');
    } catch (err) {
      console.error('Failed to delete dimension:', err);
      toast.error('Failed to delete dimension');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading dimension...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dimension) {
    return (
      <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
        <div className="flex items-center justify-center h-full">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load dimension. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/develop">Develop</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/develop/semantic-layer/dimensions">Dimensions</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{dimension.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {/* Page Title */}
          <div className="flex items-center gap-2">
            <Ruler className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-xl font-semibold text-foreground">{dimension.name}</h1>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/develop/semantic-layer/dimensions')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {!isTemplateLocked && isInDraftMode && !isDateDimension && (
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          {!isTemplateLocked && !readOnly && (
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
          )}
        </div>
      </div>

      <ReadOnlyBanner />

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isTemplateLocked && (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-4 mb-6 max-w-2xl">
            <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              This dimension is managed by template{templateName ? ` "${templateName}"` : ''} and cannot be edited.
            </p>
          </div>
        )}
        <div className={`max-w-2xl space-y-8${isTemplateLocked ? ' pointer-events-none opacity-60' : ''}`}>
          {isDateDimension && (
            <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-4">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-300">
                This is a system-managed date dimension. It cannot be deleted or pointed at a different entity. You can update its name, column name, and description.
              </p>
            </div>
          )}
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">General</CardTitle>
              <CardDescription>
                Basic information about this dimension.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Dimension Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter dimension name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="column_name">Column Name</Label>
                  <Input
                    id="column_name"
                    value={formData.column_name}
                    onChange={(e) => handleInputChange('column_name', e.target.value)}
                    placeholder="Enter column name for SQL"
                  />
                  <p className="text-xs text-muted-foreground">
                    The column name used in generated SQL queries.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter a description for this dimension"
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Source Configuration - hidden for DATE_DIMENSION dimensions */}
          {!isDateDimension && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Source Configuration</CardTitle>
              <CardDescription>
                Select the entity and column that this dimension is based on.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Entity</Label>
                  <Combobox
                    options={entityOptions}
                    value={formData.pipeline_node_id || ''}
                    onValueChange={handleEntityChange}
                    placeholder="Select an entity..."
                    emptyText="No entities found."
                    isLoading={nodesLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Column</Label>
                  <Combobox
                    options={columnOptions}
                    value={formData.field_id || ''}
                    onValueChange={handleColumnChange}
                    placeholder="Select a column..."
                    emptyText="No columns found."
                    disabled={!formData.pipeline_node_id}
                    isLoading={selectedNodeLoading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Super Datasets */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Super Datasets</CardTitle>
              <CardDescription>
                Control how this dimension interacts with dashboard super datasets.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="include_in_supersets">Always include in super datasets</Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this dimension will be included as a grain in all dashboard super datasets even if it is not used by any chart. This is useful for dimensions that have per-user filters so that row-level filtering can be applied at query time.
                  </p>
                </div>
                <Switch
                  id="include_in_supersets"
                  checked={formData.include_in_supersets ?? false}
                  onCheckedChange={(checked) => {
                    setFormData(prev => ({ ...prev, include_in_supersets: checked }));
                    setHasChanges(true);
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Dimension</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{dimension.name}"? This dimension may be used in charts, dashboards, or other areas of the application. Deleting it could cause errors in those places.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Dimension'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
